import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import eventService from '../services/eventService';
import dashboardService from '../services/dashboardService';
import { isEarlyBirdActive } from '../utils/registration';

// Ref for search input auto-focus
const SEARCH_INPUT_ID = 'events-search-input';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function EventList() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const canCreateEvent = user?.role === 'organizer' || user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const eventsSectionRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registeredEventIds, setRegisteredEventIds] = useState(new Set());
  const [dashboardData, setDashboardData] = useState(null);

  // Search and filter state
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'date_asc');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'upcoming');
  const [showFilters, setShowFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(() => !!searchParams.get('search'));
  const searchInputRef = useRef(null);

  // Debounced search
  const debouncedSearch = useDebounce(search, 300);
  const isInitialMount = useRef(true);

  // Auto-focus search input when expanded
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);

  // Update URL params when filters change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (sortBy !== 'date_asc') params.set('sort', sortBy);
    if (statusFilter !== 'upcoming') params.set('status', statusFilter);

    setSearchParams(params, { replace: true });
  }, [debouncedSearch, sortBy, statusFilter, setSearchParams]);

  // Fetch user's registered events + dashboard data
  useEffect(() => {
    if (!user) {
      setRegisteredEventIds(new Set());
      setDashboardData(null);
      return;
    }
    eventService.getUserRegisteredEvents().then((data) => {
      setRegisteredEventIds(new Set(data.events.map((e) => e.id)));
    }).catch(() => {});
    dashboardService.getAttendeeDashboard().then((res) => {
      setDashboardData(res.data);
    }).catch(() => {});
  }, [user?.id]);

  // Fetch events
  useEffect(() => {
    fetchEvents();
  }, [debouncedSearch, statusFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        search: debouncedSearch || undefined,
        upcoming: statusFilter === 'upcoming' ? true : statusFilter === 'past' ? false : undefined,
      };

      const data = await eventService.getPublicEvents(params);
      setEvents(data.events);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Sort events client-side
  const sortedEvents = useMemo(() => {
    const sorted = [...events];
    switch (sortBy) {
      case 'date_asc':
        return sorted.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      case 'date_desc':
        return sorted.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      case 'name_asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name_desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'popular':
        return sorted.sort((a, b) => (b.attendee_count || 0) - (a.attendee_count || 0));
      default:
        return sorted;
    }
  }, [events, sortBy]);

  const clearAllFilters = useCallback(() => {
    setSearch('');
    setSortBy('date_asc');
    setStatusFilter('upcoming');
  }, []);

  // Dashboard stats (authenticated users)
  const dashboardStats = useMemo(() => {
    if (!dashboardData?.events) return null;
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const upcomingEvents = dashboardData.events.filter((e) => new Date(e.end_date) >= now);
    const totalSessions = dashboardData.events.reduce((sum, e) => sum + (e.sessions?.length || 0), 0);
    const startingThisWeek = upcomingEvents.filter((e) => {
      const start = new Date(e.start_date);
      return start >= now && start <= weekFromNow;
    }).length;
    return { upcomingEvents: upcomingEvents.length, totalSessions, startingThisWeek, events: upcomingEvents };
  }, [dashboardData]);

  // Hero step click handlers
  const handleFindEvent = useCallback(() => {
    setSearchExpanded(true);
    eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleRegisterSessions = useCallback(() => {
    eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleViewSchedule = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Find first registered event slug from sorted events
    const registeredEvent = sortedEvents.find((e) => registeredEventIds.has(e.id));
    if (registeredEvent) {
      navigate(`/events/${registeredEvent.slug}/schedule`);
    } else {
      // Authenticated but no registrations — scroll to events
      eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isAuthenticated, navigate, sortedEvents, registeredEventIds]);

  const formatDateCompact = (date) => {
    const d = new Date(date);
    return {
      month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: d.getDate(),
    };
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const opts = { month: 'short', day: 'numeric' };

    if (startDate.toDateString() === endDate.toDateString()) {
      return startDate.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
    }
    return `${startDate.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
  };

  const isPastEvent = (endDate) => new Date(endDate) < new Date();

  const getRegistrationBadge = (event) => {
    if (isPastEvent(event.end_date)) return null;
    // Show "Registered" badge if user is registered for this event
    if (registeredEventIds.has(event.id)) {
      return { className: 'registered', text: 'Registered', showDot: false, showCheck: true };
    }
    // Early bird active — show special badge (takes priority over closed/paused)
    if (isEarlyBirdActive(event)) {
      return { className: 'early-bird', text: 'Early Bird Open', showDot: true };
    }
    const status = event.registration_status;
    if (!status || status === 'closed') {
      return status === 'closed' ? { className: 'closed', text: 'Registration Closed', showDot: false } : null;
    }
    if (status === 'paused') {
      return { className: 'paused', text: 'Registration Paused', showDot: false };
    }
    // Open — check if closing soon
    if (event.registration_closes_at) {
      const closes = new Date(event.registration_closes_at);
      const now = new Date();
      const daysUntilClose = (closes - now) / (1000 * 60 * 60 * 24);
      if (daysUntilClose > 0 && daysUntilClose <= 7) {
        const closeText = closes.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return { className: 'open', text: `Closes ${closeText}`, showDot: true };
      }
    }
    return { className: 'open', text: 'Registration Open', showDot: true };
  };

  return (
    <div className="event-list-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Geist:wght@300;400;500;600&display=swap');

        .event-list-page {
          min-height: 100vh;
          background: #f8f7f4;
          font-family: 'Geist', system-ui, sans-serif;
          position: relative;
        }

        .event-list-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          opacity: 0.03;
          pointer-events: none;
          z-index: 0;
        }

        /* Hero Section */
        .hero {
          position: relative;
          padding: 3rem 2rem 2.5rem;
          overflow: hidden;
          background: linear-gradient(180deg, #ffffff 0%, #f8f7f4 100%);
          border-bottom: 1px solid #e7e5e0;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hero-text {
          max-width: 640px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.875rem;
          background: rgba(2, 136, 209, 0.08);
          border: 1px solid rgba(2, 136, 209, 0.18);
          border-radius: 100px;
          color: #0277bd;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 1.25rem;
        }

        .hero-title {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 400;
          color: #1e293b;
          line-height: 1.2;
          margin: 0 0 1rem;
          letter-spacing: -0.02em;
        }

        .hero-title em {
          font-style: italic;
          color: #0288d1;
        }

        .hero-subtitle {
          font-size: 1.0625rem;
          color: #64748b;
          line-height: 1.6;
          margin: 0 0 2rem;
        }

        .hero-steps {
          display: flex;
          gap: 0;
          margin-bottom: 2rem;
          align-items: flex-start;
        }

        .hero-step-arrow {
          display: flex;
          align-items: center;
          padding-top: 2.25rem;
          color: #cbd5e1;
          flex-shrink: 0;
        }

        .hero-step-arrow svg {
          width: 24px;
          height: 24px;
        }

        .hero-step-number {
          width: 22px;
          height: 22px;
          background: #0288d1;
          color: white;
          border-radius: 50%;
          font-size: 0.6875rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.375rem;
          flex-shrink: 0;
        }

        .hero-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
          cursor: pointer;
          padding: 0.75rem;
          border-radius: 12px;
          border: none;
          background: none;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .hero-step:hover {
          background: rgba(2, 136, 209, 0.04);
          transform: translateY(-2px);
        }

        .hero-step:focus-visible {
          outline: 2px solid #0288d1;
          outline-offset: 2px;
          background: rgba(2, 136, 209, 0.04);
        }

        .hero-step:hover .hero-step-icon {
          background: rgba(2, 136, 209, 0.14);
          box-shadow: 0 2px 8px rgba(2, 136, 209, 0.15);
        }

        .hero-step-icon {
          width: 44px;
          height: 44px;
          background: rgba(2, 136, 209, 0.08);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.25rem;
          transition: all 0.2s ease;
        }

        .hero-step-icon svg {
          width: 22px;
          height: 22px;
          color: #0288d1;
        }

        .hero-step-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #1e293b;
        }

        .hero-step-desc {
          font-size: 0.75rem;
          color: #94a3b8;
          line-height: 1.4;
        }

        .hero-cta-group {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.625rem;
        }

        .hero-signin-link {
          font-size: 0.8125rem;
          color: #64748b;
          text-decoration: none;
          transition: color 0.2s;
        }

        .hero-signin-link:hover {
          color: #0288d1;
        }

        .btn-ghost {
          padding: 0.625rem 1.25rem;
          background: white;
          border: 1px solid #d6d3d1;
          color: #1e293b;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }

        .btn-ghost:hover {
          background: #f8f7f4;
          border-color: #a8a29e;
        }

        .btn-primary {
          padding: 0.625rem 1.25rem;
          background: #0288d1;
          border: none;
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          box-shadow: 0 2px 8px rgba(2, 136, 209, 0.25);
        }

        .btn-primary:hover {
          background: #0277bd;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(2, 136, 209, 0.35);
        }

        /* Compact Search Toolbar */
        .search-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          background: #f8f7f4;
          border: 1px solid #e7e5e0;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          color: #64748b;
          flex-shrink: 0;
        }

        .search-toggle:hover {
          background: #f1f0ed;
          color: #1e293b;
          border-color: #d6d3d1;
        }

        .search-toggle.active {
          background: #0288d1;
          border-color: #0288d1;
          color: white;
        }

        .search-toggle svg {
          width: 18px;
          height: 18px;
        }

        .search-inline-input {
          flex: 1;
          min-width: 160px;
          max-width: 280px;
          padding: 0.5rem 0.75rem;
          background: white;
          border: 1px solid #d6d3d1;
          border-radius: 8px;
          font-size: 0.8125rem;
          font-family: inherit;
          color: #1e293b;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .search-inline-input::placeholder {
          color: #94a3b8;
        }

        .search-inline-input:focus {
          outline: none;
          border-color: #0288d1;
          box-shadow: 0 0 0 3px rgba(2, 136, 209, 0.1);
        }

        .search-close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: none;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          color: #94a3b8;
          padding: 0;
          flex-shrink: 0;
          transition: all 0.15s;
        }

        .search-close-btn:hover {
          background: #f1f0ed;
          color: #64748b;
        }

        .search-close-btn svg {
          width: 16px;
          height: 16px;
        }

        .filter-chips {
          display: flex;
          gap: 0.375rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .filter-chip {
          padding: 0.4375rem 0.75rem;
          background: #f8f7f4;
          border: 1px solid #e7e5e0;
          border-radius: 8px;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }

        .filter-chip:hover {
          background: #f1f0ed;
          color: #1e293b;
        }

        .filter-chip.active {
          background: #1e293b;
          border-color: #1e293b;
          color: white;
        }

        .filter-chip.accent {
          background: linear-gradient(135deg, #29b6f6 0%, #03a9f4 100%);
          color: white;
          border-color: transparent;
          box-shadow: 0 2px 12px rgba(41, 182, 246, 0.25);
        }

        /* Sort dropdown */
        .sort-select {
          padding: 0.4375rem 2rem 0.4375rem 0.75rem;
          background: #f8f7f4 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 0.5rem center;
          border: 1px solid #e7e5e0;
          border-radius: 8px;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #1e293b;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
          -webkit-appearance: none;
          appearance: none;
        }

        .sort-select:hover {
          background-color: #f1f0ed;
        }

        .sort-select:focus {
          outline: none;
          border-color: #0288d1;
        }

        /* Events Grid */
        .events-section {
          position: relative;
          z-index: 5;
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 2rem 4rem;
        }

        .events-header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .events-section-heading {
          margin-right: auto;
        }

        .events-section-title {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.5rem;
          font-weight: 400;
          color: #1e293b;
          margin: 0;
          line-height: 1.3;
        }

        .events-section-sub {
          font-size: 0.8125rem;
          color: #64748b;
          margin: 0.25rem 0 0;
        }

        .events-toolbar {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .events-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 1.5rem;
        }

        /* Event Card */
        .event-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          text-decoration: none;
          display: flex;
          flex-direction: column;
          animation: cardFadeIn 0.5s ease-out backwards;
        }

        .event-card:nth-child(1) { animation-delay: 0.05s; }
        .event-card:nth-child(2) { animation-delay: 0.1s; }
        .event-card:nth-child(3) { animation-delay: 0.15s; }
        .event-card:nth-child(4) { animation-delay: 0.2s; }
        .event-card:nth-child(5) { animation-delay: 0.25s; }
        .event-card:nth-child(6) { animation-delay: 0.3s; }

        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .event-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
        }

        .event-card-image {
          position: relative;
          height: 120px;
          overflow: hidden;
        }

        .event-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .event-card:hover .event-card-image img {
          transform: scale(1.05);
        }

        .event-card-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, var(--brand-color, #1e293b) 0%, color-mix(in srgb, var(--brand-color, #1e293b) 80%, black) 100%);
        }

        .event-card-image-placeholder svg {
          width: 48px;
          height: 48px;
          color: rgba(255,255,255,0.2);
        }

        .event-card-date {
          position: absolute;
          top: 1rem;
          left: 1rem;
          background: white;
          border-radius: 10px;
          padding: 0.5rem 0.75rem;
          text-align: center;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          min-width: 52px;
        }

        .event-card-date-month {
          font-size: 0.625rem;
          font-weight: 600;
          color: #0288d1;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .event-card-date-day {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.2;
        }

        .event-card-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          padding: 0.375rem 0.75rem;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(8px);
          border-radius: 6px;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: white;
        }

        .event-card-badge.past {
          background: rgba(100, 116, 139, 0.8);
        }

        /* Registration status badge - bottom right of card image */
        .event-card-reg-badge {
          position: absolute;
          bottom: 0.75rem;
          right: 0.75rem;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.75rem;
          backdrop-filter: blur(8px);
          border-radius: 6px;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: white;
        }

        .event-card-reg-badge.open {
          background: rgba(22, 163, 74, 0.85);
        }

        .event-card-reg-badge.paused {
          background: rgba(217, 119, 6, 0.85);
        }

        .event-card-reg-badge.closed {
          background: rgba(100, 116, 139, 0.8);
        }

        .event-card-reg-badge.registered {
          background: rgba(245, 158, 11, 0.9);
          color: #1c1917;
        }

        .event-card-reg-badge.early-bird {
          background: rgba(217, 119, 6, 0.9);
        }

        .event-card-reg-badge.early-bird .pulse-dot {
          background: #fde68a;
          box-shadow: 0 0 6px rgba(253, 230, 138, 0.6);
        }

        .event-card-reg-badge .pulse-dot {
          width: 6px;
          height: 6px;
          background: #86efac;
          border-radius: 50%;
          animation: pulse 2s infinite;
          box-shadow: 0 0 6px rgba(134, 239, 172, 0.6);
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.9); }
        }

        .event-card-cta.register {
          color: #b45309;
          font-weight: 700;
        }

        .event-card-body {
          padding: 1.25rem 1.5rem 1.5rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .event-card-title {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.375rem;
          font-weight: 400;
          color: #1e293b;
          margin: 0 0 0.75rem;
          line-height: 1.3;
          transition: color 0.2s;
        }

        .event-card:hover .event-card-title {
          color: #0288d1;
        }

        .event-card-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .event-card-meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8125rem;
          color: #64748b;
        }

        .event-card-meta-item svg {
          width: 16px;
          height: 16px;
          color: #94a3b8;
          flex-shrink: 0;
        }

        .event-card-footer {
          margin-top: auto;
          padding-top: 1rem;
          border-top: 1px solid #f1f0ed;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .event-card-attendees {
          font-size: 0.8125rem;
          color: #64748b;
        }

        .event-card-cta {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #0288d1;
          transition: gap 0.2s;
        }

        .event-card:hover .event-card-cta {
          gap: 0.625rem;
        }

        .event-card-cta svg {
          width: 16px;
          height: 16px;
        }

        /* Preview button on event card */
        .event-card-preview {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.3125rem 0.625rem;
          background: none;
          border: 1px solid #d6d3d1;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
          text-decoration: none;
          flex-shrink: 0;
        }

        .event-card-preview:hover {
          background: #f8f7f4;
          border-color: #0288d1;
          color: #0288d1;
        }

        .event-card-preview svg {
          width: 14px;
          height: 14px;
        }

        /* Loading Skeleton */
        .skeleton-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
        }

        .skeleton-image {
          height: 120px;
          background: linear-gradient(90deg, #f1f0ed 25%, #e8e7e4 50%, #f1f0ed 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-body {
          padding: 1.25rem 1.5rem 1.5rem;
        }

        .skeleton-line {
          height: 1rem;
          background: linear-gradient(90deg, #f1f0ed 25%, #e8e7e4 50%, #f1f0ed 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 0.75rem;
        }

        .skeleton-line.title {
          height: 1.5rem;
          width: 80%;
        }

        .skeleton-line.short {
          width: 60%;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .empty-state-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 1.5rem;
          background: #f1f0ed;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .empty-state-icon svg {
          width: 36px;
          height: 36px;
          color: #94a3b8;
        }

        .empty-state-title {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.5rem;
          color: #1e293b;
          margin: 0 0 0.5rem;
        }

        .empty-state-text {
          color: #64748b;
          margin: 0 0 1.5rem;
        }

        /* Error State */
        .error-banner {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 2rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .error-banner svg {
          width: 20px;
          height: 20px;
          color: #ef4444;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .error-banner-text {
          color: #991b1b;
          font-size: 0.875rem;
        }

        .error-banner-text strong {
          display: block;
          margin-bottom: 0.25rem;
        }

        .btn-lg {
          padding: 0.875rem 1.75rem;
          font-size: 1rem;
        }

        /* Dashboard Hero (authenticated) */
        .dash-hero {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .dash-greeting {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: clamp(1.75rem, 3.5vw, 2.5rem);
          font-weight: 400;
          color: #1e293b;
          margin: 0 0 0.375rem;
          letter-spacing: -0.02em;
        }

        .dash-subline {
          font-size: 1rem;
          color: #64748b;
          margin: 0 0 1.75rem;
        }

        .dash-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .dash-stat-card {
          background: white;
          border: 1px solid #e7e5e0;
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
          transition: all 0.2s;
        }

        .dash-stat-card:hover {
          border-color: #cbd5e1;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        .dash-stat-value {
          font-size: 2rem;
          font-weight: 600;
          color: #0288d1;
          line-height: 1;
          margin-bottom: 0.375rem;
        }

        .dash-stat-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .dash-next-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 0.75rem;
        }

        .dash-next-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.75rem;
        }

        .dash-event-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: white;
          border: 1px solid #e7e5e0;
          border-radius: 12px;
          padding: 0.875rem 1.25rem;
          text-decoration: none;
          transition: all 0.2s;
        }

        .dash-event-row:hover {
          border-color: #cbd5e1;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          transform: translateY(-1px);
        }

        .dash-event-date {
          background: #f1f5f9;
          border-radius: 8px;
          padding: 0.375rem 0.625rem;
          text-align: center;
          min-width: 48px;
          flex-shrink: 0;
        }

        .dash-event-date-month {
          font-size: 0.5625rem;
          font-weight: 600;
          color: #0288d1;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .dash-event-date-day {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.2;
        }

        .dash-event-info {
          flex: 1;
          min-width: 0;
        }

        .dash-event-name {
          font-weight: 600;
          font-size: 0.9375rem;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .dash-event-meta {
          font-size: 0.75rem;
          color: #94a3b8;
          margin-top: 0.125rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .dash-event-meta-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .dash-event-meta-item svg {
          width: 13px;
          height: 13px;
        }

        .dash-reg-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.625rem;
          background: rgba(245, 158, 11, 0.12);
          color: #b45309;
          border-radius: 100px;
          font-size: 0.6875rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .dash-reg-pill svg {
          width: 12px;
          height: 12px;
        }

        .dash-cta-row {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero {
            padding: 1.5rem 1rem 2rem;
          }

          .hero-title {
            font-size: 1.75rem;
          }

          .hero-subtitle {
            margin-bottom: 1.25rem;
          }

          .hero-steps {
            flex-direction: column;
            gap: 0;
            margin-bottom: 1.25rem;
          }

          .hero-step-arrow {
            padding-top: 0;
            padding: 0.25rem 0;
            transform: rotate(90deg);
          }

          .hero-step-arrow svg {
            width: 20px;
            height: 20px;
          }

          .hero-step {
            flex-direction: row;
            text-align: left;
            gap: 0.75rem;
          }

          .hero-step-number {
            display: none;
          }

          .hero-step-icon {
            margin-bottom: 0;
            flex-shrink: 0;
            position: relative;
          }

          .hero-step-icon::before {
            content: attr(data-step);
            position: absolute;
            top: -6px;
            right: -6px;
            width: 18px;
            height: 18px;
            background: #0288d1;
            color: white;
            border-radius: 50%;
            font-size: 0.625rem;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .hero-step-text {
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
          }

          .events-section {
            padding: 1.5rem 1rem 3rem;
          }

          .events-header {
            gap: 0.5rem;
          }

          .events-toolbar {
            width: 100%;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 0.25rem;
            -webkit-overflow-scrolling: touch;
          }

          .filter-chips {
            flex-wrap: nowrap;
          }

          .filter-chip {
            flex-shrink: 0;
          }

          .events-grid {
            grid-template-columns: 1fr;
          }

          .event-card-image {
            height: 90px;
          }

          .dash-stats {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.625rem;
          }

          .dash-stat-card {
            padding: 1rem 0.75rem;
          }

          .dash-stat-value {
            font-size: 1.5rem;
          }

          .dash-stat-label {
            font-size: 0.6875rem;
          }

          .dash-event-row {
            flex-wrap: wrap;
            gap: 0.625rem;
          }

          .dash-cta-row {
            flex-direction: column;
          }

          .dash-cta-row .btn-primary,
          .dash-cta-row .btn-ghost {
            width: 100%;
            text-align: center;
            justify-content: center;
          }
        }
      `}</style>

      {/* Hero */}
      <header className="hero">
        {user && dashboardStats ? (
          <div className="dash-hero">
            <h1 className="dash-greeting">
              Welcome back, {user.first_name}
            </h1>
            <p className="dash-subline">
              You have {dashboardStats.upcomingEvents} upcoming event{dashboardStats.upcomingEvents !== 1 ? 's' : ''} and {dashboardStats.totalSessions} registered session{dashboardStats.totalSessions !== 1 ? 's' : ''}
            </p>

            {dashboardStats.upcomingEvents === 0 && dashboardStats.totalSessions === 0 && dashboardStats.startingThisWeek === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.9375rem', margin: '0 0 2rem' }}>
                You haven't registered for any sessions yet. Browse an event below to get started.
              </p>
            ) : (
              <div className="dash-stats">
                <div className="dash-stat-card">
                  <div className="dash-stat-value">{dashboardStats.upcomingEvents}</div>
                  <div className="dash-stat-label">Upcoming Events</div>
                </div>
                <div className="dash-stat-card">
                  <div className="dash-stat-value">{dashboardStats.totalSessions}</div>
                  <div className="dash-stat-label">Registered Sessions</div>
                </div>
                <div className="dash-stat-card">
                  <div className="dash-stat-value">{dashboardStats.startingThisWeek}</div>
                  <div className="dash-stat-label">Starting This Week</div>
                </div>
              </div>
            )}

            {dashboardStats.events.length > 0 && (
              <>
                <div className="dash-next-label">Your next events</div>
                <div className="dash-next-list">
                  {dashboardStats.events.slice(0, 3).map((event) => {
                    const dateInfo = formatDateCompact(event.start_date);
                    return (
                      <Link
                        key={event.id}
                        to={`/events/${event.slug}/schedule`}
                        className="dash-event-row"
                      >
                        <div className="dash-event-date">
                          <div className="dash-event-date-month">{dateInfo.month}</div>
                          <div className="dash-event-date-day">{dateInfo.day}</div>
                        </div>
                        <div className="dash-event-info">
                          <div className="dash-event-name">{event.name}</div>
                          <div className="dash-event-meta">
                            {event.location && (
                              <span className="dash-event-meta-item">
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {event.location}
                              </span>
                            )}
                            <span className="dash-event-meta-item">
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                              {event.sessions?.length || 0} session{(event.sessions?.length || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="dash-reg-pill">
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          Registered
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}

            <div className="dash-cta-row">
              {dashboardStats.totalSessions > 0 && (
                <button className="btn-primary" onClick={handleViewSchedule}>
                  View my schedule
                </button>
              )}
              <button
                className={dashboardStats.totalSessions > 0 ? 'btn-ghost' : 'btn-primary'}
                onClick={() => eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
              >
                Browse more events
              </button>
            </div>
          </div>
        ) : !user ? (
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-eyebrow">
                Event Scheduling Platform
              </div>
              <h1 className="hero-title">
                Find your event.<br />
                Build your <em>schedule.</em>
              </h1>
              <p className="hero-subtitle">
                Browse upcoming events, register for sessions, and build your personal schedule.
              </p>

              <div className="hero-steps">
                <div
                  className="hero-step"
                  onClick={handleFindEvent}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFindEvent(); } }}
                >
                  <div className="hero-step-number">1</div>
                  <div className="hero-step-icon" data-step="1">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                  </div>
                  <div className="hero-step-text">
                    <div className="hero-step-label">Find Your Event</div>
                    <div className="hero-step-desc">Search and browse events</div>
                  </div>
                </div>

                <div className="hero-step-arrow" aria-hidden="true">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </div>

                <div
                  className="hero-step"
                  onClick={handleRegisterSessions}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRegisterSessions(); } }}
                >
                  <div className="hero-step-number">2</div>
                  <div className="hero-step-icon" data-step="2">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                  </div>
                  <div className="hero-step-text">
                    <div className="hero-step-label">Register for Sessions</div>
                    <div className="hero-step-desc">Pick the sessions you want</div>
                  </div>
                </div>

                <div className="hero-step-arrow" aria-hidden="true">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </div>

                <div
                  className="hero-step"
                  onClick={handleViewSchedule}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewSchedule(); } }}
                >
                  <div className="hero-step-number">3</div>
                  <div className="hero-step-icon" data-step="3">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                    </svg>
                  </div>
                  <div className="hero-step-text">
                    <div className="hero-step-label">View Your Schedule</div>
                    <div className="hero-step-desc">Your personal agenda, ready to go</div>
                  </div>
                </div>
              </div>

              <div className="hero-cta-group">
                <button
                  className="btn-primary btn-lg"
                  onClick={() => eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Browse Events
                </button>
                <Link to="/login" className="hero-signin-link">
                  Already have an account? Sign in
                </Link>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      {/* Events */}
      <main className="events-section" ref={eventsSectionRef}>
        {error && (
          <div className="error-banner">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="error-banner-text">
              <strong>Error loading events</strong>
              {error}
            </div>
          </div>
        )}

        <div className="events-header">
          <div className="events-section-heading">
            <h2 className="events-section-title">{user ? 'All upcoming events' : 'Upcoming Events'}</h2>
            {!user && !loading && sortedEvents.length > 0 && (
              <p className="events-section-sub">Register now &mdash; spots are limited.</p>
            )}
          </div>

          {searchExpanded && (
            <div className="events-toolbar">
              <button
                className="search-toggle active"
                onClick={() => {
                  setSearch('');
                  setSearchExpanded(false);
                }}
                aria-label="Close search"
                title="Close search"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              <input
                ref={searchInputRef}
                id={SEARCH_INPUT_ID}
                type="text"
                className="search-inline-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events..."
              />

              <button
                className="search-close-btn"
                onClick={() => {
                  setSearch('');
                  setSearchExpanded(false);
                }}
                aria-label="Close search"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="filter-chips">
                <button
                  className={`filter-chip ${statusFilter === 'upcoming' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('upcoming')}
                >
                  Upcoming
                </button>
                <button
                  className={`filter-chip ${statusFilter === 'past' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('past')}
                >
                  Past
                </button>
                <button
                  className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('all')}
                >
                  All Events
                </button>

                <select
                  className="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="date_asc">Soonest First</option>
                  <option value="date_desc">Latest First</option>
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="events-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-image" />
                <div className="skeleton-body">
                  <div className="skeleton-line title" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line short" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedEvents.length > 0 ? (
          <div className="events-grid">
            {sortedEvents.map((event) => {
              const dateInfo = formatDateCompact(event.start_date);
              const past = isPastEvent(event.end_date);
              const regBadge = getRegistrationBadge(event);
              const isUserRegistered = registeredEventIds.has(event.id);
              const showRegisterCta = !past && !isUserRegistered && event.registration_status === 'open';

              return (
                <Link
                  key={event.id}
                  to={`/events/${event.slug}`}
                  className="event-card"
                >
                  <div className="event-card-image">
                    {event.cover_image_url || event.banner_url ? (
                      <img
                        src={event.cover_image_url || event.banner_url}
                        alt={event.name}
                      />
                    ) : (
                      <div
                        className="event-card-image-placeholder"
                        style={{ '--brand-color': event.brand_color || '#1e293b' }}
                      >
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
                        </svg>
                      </div>
                    )}
                    <div className="event-card-date">
                      <div className="event-card-date-month">{dateInfo.month}</div>
                      <div className="event-card-date-day">{dateInfo.day}</div>
                    </div>
                    {past && <div className="event-card-badge past">Past</div>}
                    {event.is_virtual && <div className="event-card-badge">Virtual</div>}
                    {regBadge && (
                      <div className={`event-card-reg-badge ${regBadge.className}`}>
                        {regBadge.showCheck && (
                          <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {regBadge.showDot && <span className="pulse-dot" />}
                        {regBadge.text}
                      </div>
                    )}
                  </div>

                  <div className="event-card-body">
                    <h3 className="event-card-title">{event.name}</h3>

                    {event.description && (
                      <p style={{
                        fontSize: '0.8125rem',
                        color: '#64748b',
                        lineHeight: 1.5,
                        margin: '0 0 0.75rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {event.description}
                      </p>
                    )}
                    {event.session_count != null && (
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#94a3b8',
                        margin: event.description ? '-0.5rem 0 0.75rem' : '0 0 0.75rem',
                      }}>
                        {event.session_count} session{event.session_count !== 1 ? 's' : ''}
                      </p>
                    )}

                    <div className="event-card-meta">
                      <div className="event-card-meta-item">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDateRange(event.start_date, event.end_date)}
                      </div>
                      {event.location && (
                        <div className="event-card-meta-item">
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {event.location}
                        </div>
                      )}
                    </div>

                    <div className="event-card-footer">
                      <button
                        className="event-card-preview"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/events/${event.slug}`);
                        }}
                        title="Preview event brochure"
                      >
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Preview
                      </button>
                      <span className={`event-card-cta${showRegisterCta ? ' register' : ''}`}>
                        {showRegisterCta ? 'Browse Sessions' : isUserRegistered && !past ? 'View My Schedule' : 'View Details'}
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="empty-state-title">
              {search ? 'No events found' : 'No events yet'}
            </h3>
            <p className="empty-state-text">
              {search
                ? `We couldn't find any events matching "${search}"`
                : 'Be the first to create an event!'}
            </p>
            {search ? (
              <button onClick={clearAllFilters} className="btn-ghost">
                Clear filters
              </button>
            ) : canCreateEvent ? (
              <Link to="/events/create" className="btn-primary">
                Create Event
              </Link>
            ) : !user ? (
              <Link to="/register" className="btn-primary">
                Get Started
              </Link>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}

export default EventList;
